import React from 'react';
import Kendra from 'aws-sdk/clients/kendra';
import S3 from 'aws-sdk/clients/s3';
import { SearchBar } from './search/searchBar/SearchBar';
import { Facets } from './search/facets/Facets';
import { ResultsPanel } from './search/searchBar/results-panel/ResultsPanel';
import { useTranslate } from 'src/i18n/useTranslate';
import _ from 'lodash';
import {
    Alert,
    Box,
    SpaceBetween,
    TextContent,
    Button,
    SegmentedControl,
} from '@cloudscape-design/components';
import { usePreferences } from 'src/components/providers/PreferencesProvider';
import { useKendraSearch } from './hooks/useKendraSearch';
import { ListViewIcon, GridViewIcon } from './icons/ViewIcons';
import { Spinner } from '@cloudscape-design/components';
import { SelectedFacetManager } from './search/facets/SelectedFacetManager';

interface KendraSearchIntegratedProps {
    initialQuery?: string;
    facetConfiguration?: {
        facetsToShowWhenUncollapsed: number;
        showCount: boolean;
        updateAvailableFacetsWhenFilterChange: boolean;
        facetPanelDefaultOpen: boolean;
    };
    onSearch?: (query: string) => void;
}

export const KendraSearchIntegrated = ({
    initialQuery,
    facetConfiguration,
    onSearch,
}: KendraSearchIntegratedProps) => {
    const t = useTranslate();
    const { listViewStyle, updateListViewStyleState, userPreferences } =
        usePreferences();

    const {
        dataReady,
        searchResults,
        docResults,
        currentPageNumber,
        queryText,
        error,
        attributeTypeLookup,
        availableFacets,
        selectedFacets,
        index,
        facetsOpen,
        suggestionsEnabled,
        dataSourceNameLookup,
        getResults,
        getQuerySuggestions,
        onSelectedFacetsChanged,
        handleClickExpander,
        handlePageChange,
        maxPageWithResults,
        totalDocumentCount,
    } = useKendraSearch({
        initialQuery,
        facetConfiguration,
        userLanguage: userPreferences?.language?.name,
    });

    return {
        searchBar: (
            <>
                {error && (
                    <Alert
                        type="error"
                        header={error?.message || 'An error occurred'}
                    />
                )}
                <SearchBar
                    onSubmit={(query, page) => {
                        getResults(query, page);
                        if (onSearch) onSearch(query);
                    }}
                    suggestionsEnabled={suggestionsEnabled}
                    getQuerySuggestions={getQuerySuggestions}
                    initialValue={queryText}
                />
            </>
        ),
        sidebar:
            queryText && dataReady ? (
                <div>
                    <h3>{t('filters', 'Filters')}</h3>
                    <Button
                        onClick={() =>
                            onSelectedFacetsChanged(
                                SelectedFacetManager.empty()
                            )
                        }
                    >
                        {t('clear-all', 'Clear All')}
                    </Button>
                    <Facets
                        index={index}
                        availableFacets={availableFacets}
                        selectedFacets={selectedFacets}
                        onSelectedFacetsChanged={onSelectedFacetsChanged}
                        open={facetsOpen}
                        onExpand={handleClickExpander}
                        attributeTypeLookup={attributeTypeLookup}
                        dataSourceNameLookup={dataSourceNameLookup}
                    />
                </div>
            ) : null,
        content: queryText ? (
            !dataReady ? (
                <Box textAlign="center" padding="xl">
                    <Spinner size="large" />
                    <TextContent>
                        <p>{t('searching', 'Searching...')}</p>
                    </TextContent>
                </Box>
            ) : searchResults.TotalNumberOfResults === 0 ? (
                <Box margin="xxs" textAlign="center">
                    {t(
                        'kendra-didnt-match-results',
                        "Kendra didn't match any results to your query."
                    )}
                </Box>
            ) : (
                <SpaceBetween size="m">
                    <div className="title-container">
                        <SpaceBetween
                            size="xxl"
                            direction="horizontal"
                            className="flex-row justify-space-between"
                        >
                            <TextContent>
                                <h1 style={{ marginBottom: '16px' }}>
                                    {totalDocumentCount
                                        ? t(
                                              'showing-results-for',
                                              `Showing results`,
                                              {
                                                  count: totalDocumentCount,
                                                  query: queryText,
                                              }
                                          )
                                        : t('results-for', `Results`, {
                                              query: queryText,
                                          })}
                                </h1>
                            </TextContent>
                            <SegmentedControl
                                selectedId={listViewStyle}
                                onChange={({ detail }) =>
                                    updateListViewStyleState(detail.selectedId)
                                }
                                label={t('view-toggle', 'View toggle')}
                                options={[
                                    {
                                        iconSvg: <ListViewIcon />,
                                        iconAlt: t(
                                            'view-as-list',
                                            'View as list'
                                        ),
                                        id: 'list-view',
                                    },
                                    {
                                        iconSvg: <GridViewIcon />,
                                        iconAlt: t(
                                            'view-as-grid',
                                            'View as grid'
                                        ),
                                        id: 'grid-view',
                                    },
                                ]}
                            />
                        </SpaceBetween>
                    </div>
                    <ResultsPanel
                        results={searchResults}
                        docResults={docResults}
                        dataReady={dataReady}
                        currentPageNumber={currentPageNumber}
                        viewStyle={listViewStyle}
                        queryText={queryText}
                        onPageChange={handlePageChange}
                        maxPageWithResults={maxPageWithResults}
                        totalDocumentCount={totalDocumentCount}
                    />
                </SpaceBetween>
            )
        ) : null,
    };
};
